/**
 * Pointer Input Settings sub-dialog.
 *
 * Opened from DynamicInputTab via the "Settings..." button next to the
 * Pointer Input section. Mirrors the AutoCAD 2024 "Pointer Input Settings"
 * nested dialog with three radio groups:
 *
 *   1. Format: Polar (distance < angle) vs Cartesian (X, Y) → DYNPICOORDS
 *   2. Format: Relative (@) vs Absolute (#) → DYNPIFORMAT
 *   3. Visibility: When to show the fields → DYNPIVIS (0/1/2)
 *
 * Rendered as a modal overlay inside the parent dialog portal.
 */

import { useState } from "react";

import { useT } from "../../i18n";
import { useSysvarStore } from "../sysvarStore";

export type PointerInputSettingsSubdialogProps = {
  onClose: () => void;
};

export function PointerInputSettingsSubdialog({
  onClose,
}: PointerInputSettingsSubdialogProps) {
  const t = useT();
  const values = useSysvarStore((s) => s.values);
  const setVar = useSysvarStore((s) => s.setVar);

  // Local draft so changes apply only on OK
  const [piCoords, setPiCoords] = useState(values.DYNPICOORDS);
  const [piFormat, setPiFormat] = useState(values.DYNPIFORMAT);
  const [piVis, setPiVis] = useState(values.DYNPIVIS);

  function handleOk() {
    setVar("DYNPICOORDS", piCoords);
    setVar("DYNPIFORMAT", piFormat);
    setVar("DYNPIVIS", piVis);
    onClose();
  }

  return (
    <div
      className="dsettings-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="dsettings-content dsettings-subdialog"
        role="dialog"
        aria-modal="true"
        aria-label={t("dsettings.pi.title")}
      >
        <div className="dsettings-title">{t("dsettings.pi.title")}</div>

        {/* ── Format: Polar / Cartesian ── */}
        <div className="dsettings-section">
          <div className="dsettings-section-title">
            {t("dsettings.pi.format.title")}
          </div>
          <label className="dsettings-radio-row">
            <input
              type="radio"
              name="pi-coords"
              value="polar"
              checked={piCoords === 0}
              onChange={() => setPiCoords(0)}
            />
            <span>{t("dsettings.pi.polar")}</span>
          </label>
          <label className="dsettings-radio-row">
            <input
              type="radio"
              name="pi-coords"
              value="cartesian"
              checked={piCoords === 1}
              onChange={() => setPiCoords(1)}
            />
            <span>{t("dsettings.pi.cartesian")}</span>
          </label>
        </div>

        {/* ── Format: Relative / Absolute ── */}
        <div className="dsettings-section">
          <label className="dsettings-radio-row">
            <input
              type="radio"
              name="pi-format"
              value="relative"
              checked={piFormat === 0}
              onChange={() => setPiFormat(0)}
            />
            <span>{t("dsettings.pi.relative")}</span>
          </label>
          <label className="dsettings-radio-row">
            <input
              type="radio"
              name="pi-format"
              value="absolute"
              checked={piFormat === 1}
              onChange={() => setPiFormat(1)}
            />
            <span>{t("dsettings.pi.absolute")}</span>
          </label>
        </div>

        {/* ── Visibility ── */}
        <div className="dsettings-section">
          <div className="dsettings-section-title">
            {t("dsettings.pi.vis.title")}
          </div>
          <label className="dsettings-radio-row">
            <input
              type="radio"
              name="pi-vis"
              value="2"
              checked={piVis === 2}
              onChange={() => setPiVis(2)}
            />
            <span>{t("dsettings.pi.vis.always")}</span>
          </label>
          <label className="dsettings-radio-row">
            <input
              type="radio"
              name="pi-vis"
              value="1"
              checked={piVis === 1}
              onChange={() => setPiVis(1)}
            />
            <span>{t("dsettings.pi.vis.typing")}</span>
          </label>
          <label className="dsettings-radio-row">
            <input
              type="radio"
              name="pi-vis"
              value="0"
              checked={piVis === 0}
              onChange={() => setPiVis(0)}
            />
            <span>{t("dsettings.pi.vis.command")}</span>
          </label>
        </div>

        <div className="dsettings-footer">
          <button
            type="button"
            className="dsettings-btn primary"
            onClick={handleOk}
          >
            {t("dsettings.ok")}
          </button>
          <button
            type="button"
            className="dsettings-btn"
            onClick={onClose}
          >
            {t("dsettings.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
